import { darken } from "polished";
import { Link } from "react-router";
import styled from "styled-components";

export const MIN_NAVIGATION_WIDTH = 280;
export const MAX_NAVIGATION_WIDTH = 640;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

export const Inner = styled.aside`
  display: none;

  @media (max-width: 1048px) {
    display: block;
    height: var(--top-navigation-height);
    width: 100%;
    display: flex;
    justify-content: space-around;
    box-shadow: 0px -5px 10px 0px rgb(0 0 0 / 25%);
    background-color: ${({ theme }) =>
      theme.colors.background.grundfos.primary};
  }
`;

export const NavigationWrapper = styled.div<{
  $appearance: "light" | "dark";
  $show: boolean;
  $width: number;
  $resizing: boolean;
}>`
  position: relative;
  height: calc(100vh);
  width: ${({ $width }) => $width}px;
  background-color: ${({ theme }) => theme.colors.background.layers[700]};
  padding-bottom: ${({ theme }) => theme.spacing[5]};
  transition: ${({ $resizing }) =>
    $resizing
      ? "none"
      : "width 0.3s ease-in-out, border-color 0.3s ease-in-out"};

  ${({ $show }) =>
    !$show &&
    `
    width: 0;
    border-color: transparent;
  `}

  &::after {
    position: absolute;
    bottom: 0;
    left: 0;
    content: "";
    width: 100%;
    height: 130px;
    pointer-events: none;

    ${({ $appearance }) =>
      $appearance === "light"
        ? `
          background: linear-gradient(
            0deg,
            rgb(255, 255, 255) 0%,
            rgba(255, 255, 255, 0) 100%
          );
      `
        : `
          background: linear-gradient(
            0deg,
            rgba(31, 37, 42, 1) 0%,
            rgba(31, 37, 42, 0) 100%
          );
      `}
  }

  @media (max-width: 1048px) {
    display: none;
  }
`;

export const NavigationInner = styled.div<{ $show: boolean; $width: number }>`
  transform: translate3d(0, 0, 0);
  will-change: transform;
  transition: transform 0.3s ease-in-out;
  height: 100vh;
  width: ${({ $width }) => $width}px;

  ${({ $show }) =>
    !$show &&
    `
    transform: translate3d(-100%, 0, 0);
  `}
`;

export const ResizeHandle = styled.div<{ $resizing: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  touch-action: none;
  z-index: 1400;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 2px;
    height: 100%;
    background-color: ${({ theme, $resizing }) =>
      $resizing ? theme.colors.border.strong : "transparent"};
    transition: background-color 0.2s ease-in-out;
  }

  &:hover::after {
    background-color: ${({ theme }) => theme.colors.border.strong};
  }
`;

export const NavigationToggleButton = styled.div<{ $show: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 37px;
  right: -12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.background.layers[700]};
  border: 1px solid ${({ theme }) => theme.colors.border.weak};
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  z-index: 1500;
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.layers[800]};
  }

  svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.text.secondary.color};
  }

  ${({ $show }) =>
    !$show &&
    `
    background-color: #0f3e69;

    svg {
      color: #fff;
    }

    &:hover {
      background-color:rgb(13, 57, 97);
    }
  `}
`;

export const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--side-navigation-width);
  height: var(--side-navigation-width);
`;

export const NavLinkInner = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(var(--side-navigation-width) - 16px);
  height: calc(var(--side-navigation-width) - 16px);
  background-color: ${({ theme }) =>
    darken(0.04, theme.colors.background.grundfos.primary)};
  transition: background-color 0.2s ease-in-out;
  color: #fff;

  ${({ $active, theme }) =>
    $active &&
    `
   border: 2px solid ${darken(0.06, theme.colors.background.grundfos.primary)};
  `}

  &:hover {
    background-color: ${({ theme }) =>
      darken(0.06, theme.colors.background.grundfos.primary)};
  }
`;

export const SecretButton = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  cursor: pointer;
`;

export const GDSWrapper = styled.div`
  display: block;
  z-index: 1000;

  @media (max-width: 1048px) {
    display: none;
  }
`;

export const LogoutButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 34px;
  height: 34px;
  padding-right: 12px;
`;

export const LogoutModal = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 200px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.background.layers[700]};
  padding: ${({ theme }) => theme.spacing[3]};
  box-shadow: 0px 5px 20px rgba(0, 0, 0, 0.15);
  border-radius: ${({ theme }) => theme.spacing[1]};
  animation: fadeIn 0.3s ease-in-out;
  border: 1px solid ${({ theme }) => theme.colors.border.weak};
`;

export const LogoutNav = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[2]};
  background-color: ${({ theme }) => theme.colors.background.layers[700]};
  border-radius: ${({ theme }) => theme.spacing[1]};
  cursor: pointer;

  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacing[1]};
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.layers[600]};
  }
`;

export const LogoutNavText = styled.span`
  font-size: 14px;
  transition: background-color 0.2s ease-in-out;
  padding-left: ${({ theme }) => theme.spacing[3]};
`;

export const LogoutNavIcon = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const MenuItemIcon = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.spacing[1]};
  box-shadow: 0px 3px 7px rgba(0, 0, 0, 0.1);
  color: ${({ theme }) => theme.colors.text.primary.color};
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border.weak};
  width: 20px;
  min-width: 20px;
  height: 20px;
`;

export const MenuItemBadge = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: -8px;
  right: -8px;
  background-color: ${({ theme }) => theme.colors.background.layers[700]};
  width: 16px;
  height: 16px;
  border-radius: 50%;

  svg {
    width: 14px !important;
    height: 14px !important;
  }
`;
